import styles from './Plans.module.css';

import classNames from 'classnames';
import { Check } from '@phosphor-icons/react';
import { useNavigate, useSearchParams } from 'react-router';

import { useDocumentTitle } from 'src/hooks/useDocumentTitle';

import { partnerRegistrationUrl } from 'src/utils/partnerRegistration';

import { CONFIG } from 'src/config-global';

import { Stack } from 'src/components/themed/Stack';
import { Button } from 'src/components/themed/Button';
import { Typography } from 'src/components/themed/Typography';

import { AccountRole } from 'src/types/userAuth';

const PERSONAL_FEATURES = [
  'Unlimited purchases & rentals — escrow abroad and with local dealers',
  'Deals CRM — manage rentals, extend or complete, paid-days counter',
  'Notifications on every step of your deals',
];

const DEALER_FEATURES = [
  'Up to 80 cars in MyGarage',
  'Fleet management — listings, pause, disputes, estimates, rent ↔ sale, full deal control',
  'Request & overdue notifications',
  'Extended escrow with logistics & insurance partners (early access)',
  'Everything in Personal',
];

const DEALER_ROADMAP = ['Telegram labs', 'Branded dealer page', 'Verified badge'];

/**
 * Plans page — the витрина entry into registration. Each card routes to /register with
 * the chosen plan (and forwards a listing `from` if the user arrived through the gate).
 */
export const Plans = () => {
  const navigate = useNavigate();
  useDocumentTitle('Choose your plan');
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from');

  const goToRegister = (role: AccountRole) => {
    const params = new URLSearchParams({ plan: role });
    if (from) {
      params.set('from', from);
    }
    navigate(`/register?${params.toString()}`);
  };

  return (
    <div className={classNames('container', styles.container)}>
      <Stack width="100%" maxWidth="750px" gap={6} p={4} pb={20}>
        <Stack gap={2} alignItems="center" mt={4}>
          <Typography as="h1" variant="largeTitle" fontWeight="600" color="white" textAlign="center">
            Choose your plan
          </Typography>
          <Typography color="lightWhite" textAlign="center">
            Buy, rent and sell cars with on-chain escrow.
          </Typography>
        </Stack>

        <div className={styles.card}>
          <Stack gap={1}>
            <Typography variant="title2" fontWeight="600" color="white">
              Personal
            </Typography>
            <Typography variant="title3" fontWeight="600" color="accent">
              Free
            </Typography>
          </Stack>
          <FeatureList features={PERSONAL_FEATURES} />
          <Button
            title="Continue as Personal"
            variant="white"
            onClick={() => goToRegister(AccountRole.PERSONAL)}
          />
        </div>

        <div className={classNames(styles.card, styles.cardDealer)}>
          <Stack gap={1}>
            <Stack flexDirection="row" alignItems="center" justifyContent="space-between" gap={2}>
              <Typography variant="title2" fontWeight="600" color="white">
                Dealer
              </Typography>
              <span className={styles.badge}>EARLY ACCESS</span>
            </Stack>
            <Typography variant="title3" fontWeight="600" color="accent">
              €89/mo — free until billing launch
            </Typography>
          </Stack>
          <FeatureList features={DEALER_FEATURES} />
          <Stack gap={1}>
            <Typography color="lightWhite" variant="small" fontWeight="600">
              Roadmap
            </Typography>
            <Typography color="lightWhite" variant="small">
              {DEALER_ROADMAP.join(' · ')}
            </Typography>
          </Stack>
          <Button
            title="Continue as Dealer"
            variant="accent"
            onClick={() => goToRegister(AccountRole.DEALER)}
          />
        </div>

        <PartnerLine registrationUrl={partnerRegistrationUrl(CONFIG.partnerCabinetUrl)} />
      </Stack>
    </div>
  );
};

/**
 * A company that works with dealers rather than buying from them (app#321; Tim 13.10): a logistics,
 * insurance or inspection company registers in the partner cabinet, not here. A plain link — the
 * cabinet is another app on its own origin. No line at all for a build that names no cabinet.
 */
export const PartnerLine = ({ registrationUrl }: { registrationUrl: string | null }) =>
  registrationUrl ? (
    <Typography as="p" color="lightWhite" textAlign="center">
      Logistics, insurance or inspection company?{' '}
      <a className={styles.partnerLink} href={registrationUrl}>
        Become a partner
      </a>
    </Typography>
  ) : null;

const FeatureList = ({ features }: { features: string[] }) => (
  <Stack gap={2}>
    {features.map((feature) => (
      <Stack key={feature} flexDirection="row" gap={2} alignItems="flex-start">
        <Check size={18} color="#CCFF00" weight="bold" className={styles.check} />
        <Typography color="white" variant="large">
          {feature}
        </Typography>
      </Stack>
    ))}
  </Stack>
);
